// stores/breadcrumbs.ts
import { defineStore } from "pinia";
import { type TranslationKey } from "~/helpers/common-helpers";

export interface BreadcrumbItem {
  key?: string;
  label: string;
  to?: { path: string; params?: Record<string, string> };
  disabled?: boolean;
  active?: boolean;
  icon?: string;
  dynamicSegment?: string;
}

export interface DynamicSegment {
  segment: string;
  label: string;
  value: string;
}

export const BREADCRUMB_CONFIG: Record<string, { icon?: string; translationKey?: TranslationKey }> = {
  home: { icon: "home", translationKey: "BREADCRUMB_HOME" },
  datamanagement: { icon: "icon-database", translationKey: "BREADCRUMB_DATA_WAREHOUSE" },
  datamapping: { icon: "category", translationKey: "BREADCRUMB_DATA_MAPPING" },
  integrationhub: { icon: "hub", translationKey: "BREADCRUMB_INTEGRATION_HUB" },
  propertymapping: { icon: "icon-property", translationKey: "BREADCRUMB_PROPERTY_MAPPING" },
  tenancymapping: { icon: "icon-condo", translationKey: "BREADCRUMB_TENANCY_MAPPING" },
  accountmapping: { icon: "account_balance", translationKey: "BREADCRUMB_ACCOUNT_MAPPING" },
  pipelines: { icon: "graph_1", translationKey: "BREADCRUMB_PIPELINES" },
  portfolioid: { icon: "work" }, // Dynamic segment
  propertyid: { icon: "icon-property" }, // Dynamic segment
  tenancyid: { icon: "icon-condo" }, // Dynamic segment
  rentroll: { icon: "table_view", translationKey: "BREADCRUMB_RENT_ROLL" },
  assets: { icon: "location_city", translationKey: "BREADCRUMB_ASSETS" },
  opextracker: { icon: "rubric", translationKey: "BREADCRUMB_OPEX" },
  analytics: { icon: "area_chart", translationKey: "BREADCRUMB_ANALYTICS" },
  accounts: { icon: "account_balance", translationKey: "BREADCRUMB_ACCOUNTS" },
  settings: { icon: "manage_accounts", translationKey: "BREADCRUMB_PERSONAL_SETTINGS" },
  reports: { icon: "bar_chart", translationKey: "BREADCRUMB_REPORTS" },
  layoutid: { icon: "bar_chart" }, // Dynamic segment — label set via setDynamicLabel
  overview: { translationKey: "BREADCRUMB_OVERVIEW" },
  companies: { translationKey: "BREADCRUMB_COMPANIES" },
  properties: { translationKey: "BREADCRUMB_PROPERTIES" },
  units: { translationKey: "BREADCRUMB_TENANCIES" },
  tenant: { translationKey: "BREADCRUMB_TENANT" },
  capex: { translationKey: "BREADCRUMB_CAPEX" },
  sales: { translationKey: "BREADCRUMB_SALES" },
  valuations: { translationKey: "BREADCRUMB_VALUATIONS" },
  opexes: { translationKey: "BREADCRUMB_OPEXES" },
  capexes: { translationKey: "BREADCRUMB_CAPEX" },
  balance: { translationKey: "BREADCRUMB_BALANCE" },
  actionplans: { translationKey: "BREADCRUMB_ACTION_PLANS" },
};

export const EXCLUDED_ROUTES = ["/portfolio", "/portfolio/tenancy", "/portfolio/property"];

export const useBreadcrumbsStore = defineStore("breadcrumbs", {
  state: () => ({
    items: [] as BreadcrumbItem[],
    dynamicLabels: {} as Record<string, string>,
  }),

  actions: {
    setBreadcrumbs(items: BreadcrumbItem[]) {
      this.items = items.map((item, index, array) => ({
        ...item,
        active: index === array.length - 1,
        label: this.resolveLabel(item),
      }));
    },

    clearBreadcrumbs() {
      this.items = [];
      this.dynamicLabels = {};
    },

    setDynamicLabel(segment: string, label: string) {
      this.dynamicLabels[segment] = label;

      // Update the labels of the existing breadcrumbs
      this.setBreadcrumbs(this.items);
    },

    resolveLabel(item: BreadcrumbItem): string {
      if (!item.dynamicSegment) {
        return item.label;
      }

      return this.dynamicLabels[item.dynamicSegment] || item.label;
    },
  },
});
